// frontend/src/pages/client/ClientDashboard.js
// ── DIFF vs original : ajout de l'onglet "My Aircraft" ──────────────────────
//   1. Import ClientAircraft
//   2. Ajout dans menuItems
//   3. Ajout du case dans renderContent()
// Tout le reste est identique à l'original.

import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Fuel, Wrench, FileText, Warehouse,
  Calendar, MessageSquare, User, LogOut, Menu, X,
  Bell, CreditCard, Plane
} from 'lucide-react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { getDashboardStats } from '../../services/clientApi';

// Desktop components
import ClientStatsCards    from '../../components/client/ClientStatsCards';
import ClientFuelRequests  from '../../components/client/ClientFuelRequests';
import ClientMaintenanceRequests from '../../components/client/ClientMaintenanceRequests';
import ClientInvoices      from '../../components/client/ClientInvoices';
import ClientHangars       from '../../components/client/ClientHangars';
import ClientProfile       from '../../components/client/ClientProfile';
import ClientCalendar      from '../../components/client/ClientCalendar';
import ClientChat          from '../../components/client/ClientChat';
import ClientAircraft      from '../../components/client/ClientAircraft';
import ClientDashboardHome from '../../components/client/ClientDashboardHome';

// Native mobile dashboard
import MobileClientDashboard from '../../components/client/mobile/MobileClientDashboard';

// ─── Mobile detection hook ─────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ─── Desktop View ──────────────────────────────────────────────────────────
const DesktopDashboard = () => {
  const { client, logout, hasPackage } = useClientAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const menuItems = [
    { id: 'dashboard',   label: 'Dashboard',    icon: LayoutDashboard, package: 'client_portal' },
    { id: 'aircraft',    label: 'My Aircraft',  icon: Plane,           package: 'client_portal' }, // ← NEW
    { id: 'calendar',    label: 'My calendar',  icon: Calendar,        package: 'client_portal' },
    { id: 'fuel',        label: 'Fuel',         icon: Fuel,            package: 'fuel_online' },
    { id: 'maintenance', label: 'Maintenance',  icon: Wrench,          package: 'maintenance' },
    { id: 'hangars',     label: 'My hangars',   icon: Warehouse,       package: 'hangar_lease' },
    { id: 'invoices',    label: 'Invoices',     icon: FileText,        package: 'client_portal' },
    { id: 'chat',        label: 'Chat',         icon: MessageSquare,   package: 'client_portal' },
    { id: 'profile',     label: 'My profile',   icon: User,            package: 'client_portal' },
  ];

  const visibleMenuItems = menuItems.filter(item => hasPackage(item.package));

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
  }, [activeTab]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':   
        return (
          <ClientDashboardHome 
            onNavigateToFuel={() => setActiveTab('fuel')}
          />
        );
      case 'aircraft':    return <ClientAircraft />;
      case 'calendar':    return <ClientCalendar />;
      case 'fuel':        return <ClientFuelRequests />;
      case 'maintenance': return <ClientMaintenanceRequests />;
      case 'hangars':     return <ClientHangars />;
      case 'invoices':    return <ClientInvoices />;
      case 'chat':        return <ClientChat />;
      case 'profile':     return <ClientProfile />;
      default:            return <ClientDashboardHome onNavigateToFuel={() => setActiveTab('fuel')} />;
    }
  };

  const getTabTitle = () => {
    const item = menuItems.find(m => m.id === activeTab);
    return item ? item.label : 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-gray-900/95 backdrop-blur-sm border-r border-gray-700 transition-all duration-300 z-20 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          {sidebarOpen && (
            <div>
              <h1 className="text-xl font-bold text-white">aeroTrick</h1>
              <p className="text-xs text-gray-500">Client Portal</p>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="p-3 space-y-1">
          {visibleMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
                  : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {client?.first_name?.[0]}{client?.last_name?.[0]}
              </span>
            </div>
            {sidebarOpen && (
              <div className="flex-1">
                <p className="text-sm text-white truncate">
                  {client?.first_name} {client?.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{client?.company_name || 'Client'}</p>
              </div>
            )}
            <button onClick={logout} className="text-gray-400 hover:text-white">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top bar */}
        <div className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700 px-6 py-3 flex justify-between items-center">
          <h2 className="text-white font-semibold">{getTabTitle()}</h2>
          <div className="flex items-center gap-4">
            <button className="relative text-gray-400 hover:text-white">
              <Bell size={20} />
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Plane size={16} />
              <span>Welcome, {client?.first_name}</span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

// ─── Root : automatic mobile/desktop routing ───────────────────────────────
const ClientDashboard = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileClientDashboard /> : <DesktopDashboard />;
};

export default ClientDashboard;